import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import {
  Typography, Box, IconButton, Pagination,
} from '@mui/material'
import { Settings } from '@mui/icons-material'
import DivergenceCard from '../../components/DivergenceCard'
import { selectDivergence, selectDivergenceFilters, selectPagination } from '../../DivergenceDemandsSelector'
import { actions } from '../../DivergenceDemandsState'
import { DivergenceFiltersModal } from '../../components/DivergenceFiltersModal'
import { DivergenceFilters } from '../../DivergenceDemandsTypes'
import dayjs from 'dayjs'
import * as S from './DivergenceListPage.styles'

const DivergenceListPage: React.FC = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const divergencias = useSelector(selectDivergence)
  const filters = useSelector(selectDivergenceFilters)
  const pagination = useSelector(selectPagination)
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    if (pagination?.currentPage && pagination.currentPage !== currentPage) {
      setCurrentPage(pagination.currentPage)
    }
  }, [pagination?.currentPage, currentPage])

  useEffect(() => {
    const initialFilters: DivergenceFilters = {
      status: [1],
      type: null,
      initial_date: dayjs().subtract(2, 'days').format('YYYY-MM-DD'),
      final_date: dayjs().format('YYYY-MM-DD'),
      page: 1,
      per_page: 20,
    }

    dispatch(actions.getDivergenceFilterOptions())
    dispatch(actions.setDivergenceFilters({ filters: initialFilters }))
    dispatch(actions.getDivergence({ filters: initialFilters }))
    dispatch(actions.startDivergencePolling())

    return () => {
      dispatch(actions.stopDivergencePolling())
    }
  }, [dispatch])

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value)
    const updatedFilters = {
      ...filters,
      page: value,
      per_page: filters.per_page || 20,
    }
    dispatch(actions.setDivergenceFilters({ filters: updatedFilters }))
    dispatch(actions.getDivergence({ filters: updatedFilters }))
  }

  const handleCardClick = (divergence) => {
    dispatch(actions.setActiveDivergence(
      {
        divergenceId: divergence.id,
        location: divergence.location,
        product: divergence.codProd,
        productDesc: divergence.descProd,
      },
    ))
    history.push('/restocking/divergence/badge')
  }

  const handleOpenFiltersModal = () => {
    setIsFiltersModalOpen(true)
  }

  const handleCloseFiltersModal = () => {
    setIsFiltersModalOpen(false)
  }

  const handleApplyFilters = (newFilters: DivergenceFilters) => {
    const filtersWithPagination = {
      ...newFilters,
      page: 1,
      per_page: 20,
    }
    setCurrentPage(1)
    dispatch(actions.setDivergenceFilters({ filters: filtersWithPagination }))
    dispatch(actions.getDivergence({ filters: filtersWithPagination }))
  }

  return (
    <S.Wrapper>
      <S.Header>
        <Box width='100%'>
          <Box mt={3} mb={3} textAlign='center'>
            <Typography variant='h5' fontWeight='bold' color='primary'>
              Divergências de Reestoque
            </Typography>
            <Typography variant='body1' color='text.secondary' sx={{ mt: 1 }}>
              {pagination?.totalItems || (Array.isArray(divergencias) ? divergencias.length : 0)} divergências encontradas
            </Typography>
          </Box>

          <Box display='flex' alignItems='center' justifyContent='space-between' width='100%'>
            <IconButton
              onClick={handleOpenFiltersModal}
              color='primary'
              title='Filtros'
              size='medium'
            >
              <Settings />
            </IconButton>
          </Box>
        </Box>
      </S.Header>

      <S.CardGrid>
        {Array.isArray(divergencias) ? divergencias.map((divergence, index) => (
          <DivergenceCard
            key={divergence.id || `divergence-${index}`}
            divergence={divergence}
            onClick={() => handleCardClick(divergence)}
          />
        )) : null}
      </S.CardGrid>

      {pagination && (
        <Box display='flex' flexDirection='column' alignItems='center' mt={3} gap={2}>
          <Typography variant='body2' color='text.secondary'>
            Página {pagination.currentPage || 1} de {pagination.totalPages || 1}
          </Typography>
          <Pagination
            count={pagination.totalPages || 1}
            page={pagination.currentPage || 1}
            onChange={handlePageChange}
            color='primary'
            size='large'
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      <DivergenceFiltersModal
        open={isFiltersModalOpen}
        onClose={handleCloseFiltersModal}
        filters={filters}
        onApplyFilters={handleApplyFilters}
      />
    </S.Wrapper>
  )
}

export default DivergenceListPage
